import { useState, type ReactNode } from 'react';
import { Beaker, Burette, PHMeter, Spectrophotometer } from '../lab';
import { cueIsActive, type VizCue } from '../viz/vizShared';

// Wraps a single piece of lab equipment as a standalone, narration-driven widget
// for the lecture player. The bridge does three things:
//   1. picks the instrument by `kind`,
//   2. passes config through leniently (partial configs still render), and
//   3. forwards the optional narration `cue` so the apparatus animates IN SYNC
//      with the spoken lecture (fill / titration / dip / sample driven by
//      cue.progress + cue.play), while STILL being hands-on when no cue drives it.
// onComplete fires once after the learner first interacts (or the cue first
// drives the instrument), so the player can advance.

type Kind = 'lab_beaker' | 'lab_burette' | 'ph_meter' | 'spectrophotometer';

interface Props {
  kind: Kind;
  config: Record<string, unknown>;
  onComplete?: (result: unknown) => void;
  /** Narration-sync cue forwarded by the lecture player. */
  cue?: VizCue;
}

export default function EquipmentWidget({ kind, config, onComplete, cue }: Props) {
  const [interacted, setInteracted] = useState(false);
  const fire = (result: unknown) => {
    if (!interacted) {
      setInteracted(true);
      onComplete?.(result);
    }
  };

  const num = (key: string, fallback: number) =>
    typeof config[key] === 'number' ? (config[key] as number) : fallback;
  const str = (key: string, fallback: string) =>
    typeof config[key] === 'string' ? (config[key] as string) : fallback;
  const bool = (key: string, fallback: boolean) =>
    typeof config[key] === 'boolean' ? (config[key] as boolean) : fallback;

  const driven = cueIsActive(cue);

  if (kind === 'lab_beaker') {
    return (
      <Caption text={driven ? str('label', 'Beaker') + ' — following the lecture' : str('label', 'Beaker') + ' — click to pour'}>
        <BeakerInteractive
          fillColor={str('fillColor', '#7DD3FC')}
          start={num('fillLevel', 0)}
          label={str('label', '')}
          swirl={bool('swirl', false)}
          bubbles={bool('bubbles', false)}
          precipitate={num('precipitate', 0)}
          cue={cue}
          onPour={fire}
        />
      </Caption>
    );
  }

  if (kind === 'lab_burette') {
    return (
      <Caption text={driven ? 'Titrating in sync with the lecture' : 'Open the stopcock or add a drop to titrate'}>
        <BuretteInteractive
          start={num('volume', 50)}
          maxVolume={num('maxVolume', 50)}
          liquidColor={str('liquidColor', '#7DD3FC')}
          cue={cue}
          onEmpty={fire}
        />
      </Caption>
    );
  }

  if (kind === 'ph_meter') {
    return (
      <Caption text={driven ? 'Reading pH as the lecture narrates' : 'Click the probe to dip into solution'}>
        <PHMeterInteractive reading={num('reading', 7)} cue={cue} onDip={fire} />
      </Caption>
    );
  }

  // spectrophotometer
  return (
    <Caption text={driven ? 'Measuring absorbance with the lecture' : 'Click the sample slot to insert a cuvette'}>
      <SpectroInteractive
        wavelength={num('wavelength', 540)}
        absorbance={num('absorbance', 0.421)}
        concentration={typeof config.concentration === 'number' ? (config.concentration as number) : undefined}
        cue={cue}
        onInsert={fire}
      />
    </Caption>
  );
}

function Caption({ text, children }: { text: string; children: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-1.5 rounded-md border border-ws-border bg-ws-surface p-3">
      {children}
      <p className="text-xs text-ws-ink-3">{text}</p>
    </div>
  );
}

function BeakerInteractive({
  fillColor,
  start,
  label,
  swirl,
  bubbles,
  precipitate,
  cue,
  onPour,
}: {
  fillColor: string;
  start: number;
  label: string;
  swirl: boolean;
  bubbles: boolean;
  precipitate: number;
  cue?: VizCue;
  onPour: (r: unknown) => void;
}) {
  const [fill, setFill] = useState(start);
  return (
    <Beaker
      fillLevel={fill}
      fillColor={fillColor}
      label={label}
      swirl={swirl}
      bubbles={bubbles}
      precipitate={precipitate}
      cue={cue}
      active={fill > 0}
      onClick={() => {
        const next = fill >= 100 ? 0 : Math.min(100, fill + 25);
        setFill(next);
        onPour({ fillLevel: next });
      }}
    />
  );
}

function BuretteInteractive({
  start,
  maxVolume,
  liquidColor,
  cue,
  onEmpty,
}: {
  start: number;
  maxVolume: number;
  liquidColor: string;
  cue?: VizCue;
  onEmpty: (r: unknown) => void;
}) {
  const [volume, setVolume] = useState(start);
  const [open, setOpen] = useState<'open' | 'closed'>('closed');
  return (
    <Burette
      volume={volume}
      maxVolume={maxVolume}
      liquidColor={liquidColor}
      stopcock={open}
      cue={cue}
      onStopcock={() => {
        const nextOpen = open === 'open' ? 'closed' : 'open';
        setOpen(nextOpen);
      }}
      onDispense={({ volume: v, dispensed }) => {
        setVolume(v);
        onEmpty({ volumeDispensed: dispensed });
      }}
    />
  );
}

function PHMeterInteractive({ reading, cue, onDip }: { reading: number; cue?: VizCue; onDip: (r: unknown) => void }) {
  const [inSolution, setInSolution] = useState(false);
  return (
    <PHMeter
      reading={reading}
      inSolution={inSolution}
      cue={cue}
      onDip={() => {
        setInSolution((v) => !v);
        if (!inSolution) onDip({ reading });
      }}
    />
  );
}

function SpectroInteractive({
  wavelength,
  absorbance,
  concentration,
  cue,
  onInsert,
}: {
  wavelength: number;
  absorbance: number;
  concentration?: number;
  cue?: VizCue;
  onInsert: (r: unknown) => void;
}) {
  const [sampleIn, setSampleIn] = useState(false);
  return (
    <Spectrophotometer
      wavelength={wavelength}
      absorbance={sampleIn ? absorbance : 0}
      concentration={concentration}
      sampleIn={sampleIn}
      cue={cue}
      onInsertSample={() => {
        setSampleIn((v) => !v);
        if (!sampleIn) onInsert({ wavelength, absorbance });
      }}
    />
  );
}
